/**
 * フィールド設計書の構造化テストデータ
 *
 * 構造:
 * - apps[]: アプリ一覧
 *   - app_id: アプリID
 *   - app_name: アプリ名
 *   - properties[]: フィールド一覧
 *     - label: フィールド名
 *     - required.header: ヘッダー定義 ["appId", "code", "label", "type"]
 *     - required.rows: データ行 [[appId, code, label, type]]
 *     - options: フィールドオプション
 */

export const FIELD_DESIGN_STRUCTURED_TEST_DATA = {
  apps: [
    {
      app_id: "1",
      app_name: "顧客マスタ",
      properties: [
        {
          label: "顧客名",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["1", "顧客名", "顧客名", "SINGLE_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: true,
          },
        },
        {
          label: "住所",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["1", "住所", "住所", "MULTI_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "電話番号",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["1", "電話番号", "電話番号", "SINGLE_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "メールアドレス",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["1", "メールアドレス", "メールアドレス", "LINK"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "備考",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["1", "備考", "備考", "MULTI_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
      ],
    },
    {
      app_id: "2",
      app_name: "案件管理",
      properties: [
        {
          label: "案件名",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "案件名", "案件名", "SINGLE_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: true,
          },
        },
        {
          label: "顧客名",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "顧客名", "顧客名", "LOOKUP"]],
          },
          options: {
            noLabel: false,
            required: true,
          },
        },
        {
          label: "案件概要",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "案件概要", "案件概要", "MULTI_LINE_TEXT"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "受注金額",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "受注金額", "受注金額", "NUMBER"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "受注日",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "受注日", "受注日", "DATE"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "担当者",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "担当者", "担当者", "USER_SELECT"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
        {
          label: "添付資料",
          required: {
            header: ["appId", "code", "label", "type"],
            rows: [["2", "添付資料", "添付資料", "FILE"]],
          },
          options: {
            noLabel: false,
            required: false,
          },
        },
      ],
    },
  ],
};

/**
 * API レスポンス形式（Document.content に格納される JSON 文字列）
 */
export const FIELD_DESIGN_API_RESPONSE_CONTENT = JSON.stringify(FIELD_DESIGN_STRUCTURED_TEST_DATA);

/**
 * テストデータの型定義
 */
export interface FieldDesignProperty {
  label: string;
  required: {
    header: string[];
    rows: string[][];
  };
  options: {
    noLabel: boolean;
    required: boolean;
    [key: string]: unknown;
  };
}

export interface FieldDesignApp {
  app_id: string;
  app_name: string;
  properties: FieldDesignProperty[];
}

export interface FieldDesignStructuredData {
  apps: FieldDesignApp[];
}

// ============================================================
// Redux ストア用に変換済みのデータ
// ============================================================

import { v4 as uuidv4 } from "uuid";
import type { FieldData } from "../../src/components/ui/itemModal/types";

/**
 * AppData 形式（Redux fieldDesign.appData 用）
 */
export const FIELD_DESIGN_APP_DATA = FIELD_DESIGN_STRUCTURED_TEST_DATA.apps.map((app) => ({
  app_id: app.app_id,
  app_name: app.app_name,
  properties: app.properties,
}));

/**
 * FieldData 形式（Redux fieldDesign.data 用）
 * fieldDesignSlice.ts の reducer と同じロジックで変換
 */
export const FIELD_DESIGN_FIELD_DATA: FieldData[] = (() => {
  const fieldData: FieldData[] = [];

  FIELD_DESIGN_STRUCTURED_TEST_DATA.apps.forEach((app) => {
    app.properties.forEach((prop) => {
      if (prop.required?.rows?.length > 0) {
        const row = prop.required.rows[0];
        if (row?.[0] && row[1] && row[3]) {
          fieldData.push({
            id: uuidv4(),
            app_id: row[0], // rows[0][0] = appId
            フィールド名: prop.label, // property.label
            フィールドコード: row[1], // rows[0][1] = code
            フィールドタイプ: row[3], // rows[0][3] = type
            options: prop.options || {},
          });
        }
      }
    });
  });

  return fieldData;
})();

/**
 * テストページやコンポーネントで使用する例:
 *
 * ```typescript
 * import { useDispatch } from 'react-redux';
 * import { setData, setAppData } from '~/slices/fieldDesignSlice';
 * import { FIELD_DESIGN_APP_DATA, FIELD_DESIGN_FIELD_DATA } from '@tests/fixtures';
 *
 * const dispatch = useDispatch();
 *
 * // テストデータをセット
 * dispatch(setAppData(FIELD_DESIGN_APP_DATA));
 * dispatch(setData(FIELD_DESIGN_FIELD_DATA));
 * ```
 */
